import { styled } from '@mui/material';
import { Trans } from 'react-i18next';

import { useEffect } from 'react';

import { useNavigate } from 'react-router';

import {
  Button,
  ButtonsContainer,
  Card,
  ErrorMessage,
  Typography,
} from '@app-velocity/ui';
import { TTeamInvitationDto } from '@app-velocity/av-api-v2';
import { useI18n } from '@app-velocity/i18n';
import { useAcceptInvitationMutation } from '@app-velocity/libs/teams-data-access';

const StyledNotificationCard = styled(Card)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(4),
}));

export type SingleInvitationProps = {
  invitation: TTeamInvitationDto;
};

export const SingleInvitation = ({ invitation }: SingleInvitationProps) => {
  const { t } = useI18n();
  const navigate = useNavigate();

  const {
    mutate: acceptInvitation,
    isPending,
    isSuccess,
    error,
  } = useAcceptInvitationMutation();

  useEffect(() => {
    if (!isSuccess || !invitation.teamId) return;

    navigate(`/${invitation.teamId}`);
  }, [isSuccess]);

  const handleAccept = () => {
    acceptInvitation(invitation.id!);
  };

  const getRoleLabelForInvitation = (
    teamRole: TTeamInvitationDto['teamRole']
  ) => {
    switch (teamRole) {
      case 'ADMIN':
        return t('teams:role.admin');
      case 'MEMBER':
        return t('teams:role.member');
      case 'OWNER':
        return t('teams:role.owner');
      default:
        return '-';
    }
  };

  return (
    <StyledNotificationCard>
      <Typography>
        <Trans
          i18nKey="users:notifications.invitation"
          values={{
            team: invitation.teamName,
            role: getRoleLabelForInvitation(invitation.teamRole),
          }}
          tOptions={{ interpolation: { escapeValue: false } }}
          components={{
            tag: <strong />,
          }}
        />
      </Typography>
      {!invitation.used ? (
        <ButtonsContainer>
          <Button
            size="small"
            variant="contained"
            color="primary"
            disabled={isPending}
            onClick={handleAccept}
          >
            {t('teams:invitation.accept')}
          </Button>
        </ButtonsContainer>
      ) : null}
      <ErrorMessage
        error={error}
        statusMap={{
          404: t('teams:invitation.incorrect'),
        }}
      />
    </StyledNotificationCard>
  );
};
